/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { getProductUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { Carousel, ICarouselProps } from '@msdyn365-commerce/components';
import classnames from 'classnames';
import { reaction } from 'mobx';
import * as React from 'react';

// import {thumbnailsOrientation} from '@msdyn365-commerce-modules/media-gallery';
import { IComponentNodeProps, IModuleProps, INodeProps, ISingleSlideCarouselProps, KeyCodes, NodeTag, SingleSlideCarousel } from '@msdyn365-commerce-modules/utilities';
import { IImageData, IImageDimension, IImageSettings, Image } from '@msdyn365-commerce/core';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ISmweMediaGalleryData } from './smwe-media-gallery.data';
import { ISmweMediaGalleryProps } from './smwe-media-gallery.props.autogenerated';
import { getValidProductImages } from './utils';

export interface IMediaGalleryState {
    animating: boolean;
    activeIndex: number;
    mediaGalleryItems: IImageData[] | undefined;
}

export interface IMediaGalleryThumbnailsViewProps {
    ThumbnailsContainerProps: INodeProps;
    SingleSlideCarouselComponentProps: INodeProps;

    items?: IMediaGalleryThumbnailItemViewProps[];
}

export interface IMediaGalleryThumbnailItemViewProps {
    ThumbnailItemContainerProps: INodeProps;

    Picture: React.ReactElement;
}

export interface IMediaGalleryViewProps extends ISmweMediaGalleryProps<ISmweMediaGalleryData> {
    state: IMediaGalleryState;
    productUrl?: string;
    isProductLightBox?: boolean;
    MediaGallery: IModuleProps;
    CarouselProps: INodeProps;
    Thumbnails: IMediaGalleryThumbnailsViewProps;
}

interface IImageSettingsViewports {
    xs: IImageDimension;
    sm?: IImageDimension | undefined;
    md?: IImageDimension | undefined;
    lg?: IImageDimension | undefined;
    xl?: IImageDimension | undefined;
}

const SLRAppellation: string = 'SLR Appellation';
const TastingNotes: string = 'Tasting Notes';

/**
 * Media gallery component
 */
class MediaGallery extends React.Component<ISmweMediaGalleryProps<ISmweMediaGalleryData>, IMediaGalleryState> {
    private readonly defaultGalleryImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=0&h=387&m=8', w: 0, h: 0 },
            sm: { q: 'w=0&h=387&m=8', w: 0, h: 0 },
            md: { q: 'w=0&h=772&m=8', w: 0, h: 0 },
            lg: { q: 'w=0&h=772&m=8', w: 0, h: 0 },
        },
        lazyload: true
    };

    private readonly defaultThumbnailImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=100&m=8', w: 100, h: 0 },
            lg: { q: 'w=100&m=8', w: 100, h: 0 },
        },
        lazyload: true
    };

    private noneWineProductImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=180&h=0&m=8', w: 0, h: 0 },
            sm: { q: 'w=180&h=0&m=8', w: 0, h: 0 },
            md: { q: 'w=260&h=0&m=8', w: 0, h: 0 },
            lg: { q: 'w=260&m=0', w: 0, h: 0 },
            xl: { q: 'w=260&m=0', w: 0, h: 0 },
        },
        lazyload: true
    };

    private isWineProduct: boolean = true;

    constructor(props: ISmweMediaGalleryProps<ISmweMediaGalleryData>) {
        super(props);
        this.state = { activeIndex: 0, animating: false, mediaGalleryItems: undefined };

        const modifiedImageSettings = props.config.galleryImageSettings;
        if(modifiedImageSettings) {
            if(modifiedImageSettings.viewports) {
                modifiedImageSettings.viewports = this.usePNGImageSetting(modifiedImageSettings.viewports);
                props.config.galleryImageSettings = modifiedImageSettings;
            }
        }
    }

    public usePNGImageSetting = (viewports: IImageSettingsViewports): IImageSettingsViewports => {
        const modifiedViewports = viewports;
        if(modifiedViewports?.xs?.q) {
            modifiedViewports.xs.q = modifiedViewports.xs.q.replace('f=jpg', 'f=png');
        }
        if(modifiedViewports?.sm?.q) {
            modifiedViewports.sm.q = modifiedViewports.sm.q.replace('f=jpg', 'f=png');
        }
        if(modifiedViewports?.md?.q) {
            modifiedViewports.md.q = modifiedViewports.md.q.replace('f=jpg', 'f=png');
        }
        if(modifiedViewports?.lg?.q) {
            modifiedViewports.lg.q = modifiedViewports.lg.q.replace('f=jpg', 'f=png');
        }
        if(modifiedViewports?.xl?.q) {
            modifiedViewports.xl.q = modifiedViewports.xl.q.replace('f=jpg', 'f=png');
        }

        return modifiedViewports;
    };

    public async componentDidMount(): Promise<void> {
        const isGiftCard = this.props.data.product && this.props.data.product.result ? this.props.data.product.result.ItemId?.toLowerCase() === 'giftcard' : false;
        const giftCardImage: IImageData = {src: this.props.context.app.config.giftCardImage,altText:"Gift Card" || ''};
        reaction(
            () => this.props.data.product.result ? this.props.data.product.result.RecordId : null,
            async () => {
                if (this.props.data.product.result) {
                    if (isGiftCard && this.props.context.app.config.giftCardImage) {
                        this.setState({
                            mediaGalleryItems: [giftCardImage]
                        });
                    } else {
                        this.setState({
                            mediaGalleryItems: await getValidProductImages(this.props.data.product.result.RecordId, +this.props.context.request.apiSettings.channelId, this.props.context.actionContext)
                        });
                    }
                } else {
                    this.setState({
                        mediaGalleryItems: []
                    });
                }
            }
        );

        if (this.props.data.product.result) {
            if (isGiftCard && this.props.context.app.config.giftCardImage) {
                this.setState({
                    mediaGalleryItems: [giftCardImage]
                });
            } else {
                this.setState({
                    mediaGalleryItems: await getValidProductImages(this.props.data.product.result.RecordId, +this.props.context.request.apiSettings.channelId, this.props.context.actionContext)
                });
            }
        } else {
            this.setState({
                mediaGalleryItems: []
            });
        }
    }

    public render(): JSX.Element {
        const {
            data: {
                product: {
                    result: product
                }
            },
            id,
            config,
            resources
        } = this.props;
        const data: ISmweMediaGalleryData = this.props.data;
        const wineAttributes = this.props.data.productSpecificationData.result && this.props.data.productSpecificationData.result.filter(
            attribute => attribute.Name && (attribute.Name.toUpperCase() === SLRAppellation.toUpperCase() || attribute.Name.toUpperCase() === TastingNotes.toUpperCase())
        );
        const isWineProduct = !!(wineAttributes && wineAttributes.map(attr => attr.TextValue).filter(value => value).length);
        this.isWineProduct = isWineProduct;

        if (!data) {
            throw new Error('[MediaGallery] Data items returned null or undefined');
        }

        let mediaGalleryItems = this.state.mediaGalleryItems;
        const { className, showPaginationTooltip } = config;

        if (!mediaGalleryItems && product) {
            mediaGalleryItems = [
                this._mapProductToImageData(product)
            ];
        }

        const isVertical: boolean = (config.thumbnailsOrientation === 'vertical');

        let productUrl;
        if (data.product && data.product.status === 'SUCCESS') {
            if (product) {
                productUrl = getProductUrlSync(product, this.props.context.actionContext, undefined);
            }
        }

        // tslint:disable-next-line: no-typeof-undefined
        const isProductLightbox = (typeof window !== 'undefined') && (window.location !== window.parent.location);

        const viewProps: IMediaGalleryViewProps = {
            ...(this.props as ISmweMediaGalleryProps<ISmweMediaGalleryData>),
            state: this.state,
            productUrl,
            isProductLightBox: isProductLightbox,
            MediaGallery: {
                moduleProps: this.props,
                className: classnames('ms-media-gallery', className, 'overflow-hidden',
                                      isVertical ? 'row' : 'col',
                                      isVertical ? 'media-gallery-vertical' : 'media-gallery-horizontal')
            },
            CarouselProps: {
                tag: Carousel,
                className: 'ms-media-gallery__carousel',
                items: mediaGalleryItems && mediaGalleryItems.map((item: IImageData, index: number) => this._getCarouselItem(item, index)),
                activeIndex: this.state.activeIndex,
                next: this.next,
                previous: this.previous,
                interval: false,
                directionTextPrev: resources.previousScreenshotFlipperText,
                directionTextNext: resources.nextScreenshotFlipperText,
                onIndicatorsClickHandler: this.goToIndex,
                showPaginationTooltip: showPaginationTooltip === true,
                handleOnExited: this.onExited,
                handleOnExiting: this.onExiting
            } as IComponentNodeProps<ICarouselProps>,
            Thumbnails: {
                ThumbnailsContainerProps: {
                    className: classnames('ms-media-gallery__thumbnails-container',
                                          isVertical ? 'h-100' : 'w-100',
                                          'd-flex',
                                          'justify-content-center',
                                          isVertical ? 'order-1' : 'row')
                },
                SingleSlideCarouselComponentProps: {
                    tag: SingleSlideCarousel,
                    className: 'ms-media-gallery__thumbnails',
                    vertical: isVertical,
                    flipperPrevLabel: resources.previousScreenshotFlipperText,
                    flipperNextLabel: resources.nextScreenshotFlipperText,
                    parentId: id,
                    useTabList: true,
                } as IComponentNodeProps<ISingleSlideCarouselProps>,
                items: mediaGalleryItems && mediaGalleryItems.map((item: IImageData, index: number) => this._getThumbnailItem(item, index, this.state.activeIndex)),
            }
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private onExiting = () => {
        this.setState({ animating: true });
    };

    private onExited = () => {
        this.setState({ animating: false });
    };

    private next = (): void => {
        const nextIndex = this.isLastItem() ? 0 : this.state.activeIndex + 1;
        this.goToIndex(nextIndex);
    };

    private previous = (): void => {
        const nextIndex = this.isFirstItem() ? (this.state.mediaGalleryItems ? this.state.mediaGalleryItems.length - 1 : 0) : this.state.activeIndex - 1;
        this.goToIndex(nextIndex);
    };

    private goToIndex = (index: number): void => {
        if (this.state.animating) {
            return;
        }
        this.setState({ activeIndex: index });
    };

    private _getCarouselItem = (image: IImageData, index: number): React.ReactNode => (
        <Image
            className={`ms-media-gallery__item ${this.isWineProduct ? 'wine-product' : 'non-wine-product'}`}
            {...image}
            altText={image.altText??""}
            gridSettings={this.props.context.request.gridSettings!}
            imageSettings={(this.isWineProduct || this.props.config.useAlternateImageSettings) ? this.props.config.galleryImageSettings || this.defaultGalleryImageSettings : this.noneWineProductImageSettings}
            loadFailureBehavior='default'
            id={`${this.props.id}__carousel-item__${index}`}
        />
    );

    private _getThumbnailItem = (image: IImageData, index: number, modifiedActiveIndex: number): IMediaGalleryThumbnailItemViewProps => {
        const classes = classnames(
            'ms-media-gallery__thumbnail-item',
            modifiedActiveIndex === index ? 'ms-media-gallery__thumbnail-item-active' : ''
        );

        return {
            ThumbnailItemContainerProps: {
                tag: 'li' as NodeTag,
                className: classes,
                role: 'tab',
                tabIndex: 0,
                key: index,
                'aria-label': image.altText,
                'aria-selected': modifiedActiveIndex === index,
                'aria-controls': `${this.props.id}__carousel-item__${index}`,
                onClick: this._generateOnThumbnailClick(index),
                onKeyDown: this._generateOnThumbnailKeyDown(index)
            },
            Picture: (
                <Image
                    className='ms-media-gallery__thumbnail'
                    {...image}
                    altText={image.altText??""}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={(this.isWineProduct || this.props.config.useAlternateImageSettings) ? this.props.config.thumbnailImageSettings || this.defaultThumbnailImageSettings : this.noneWineProductImageSettings}
                    loadFailureBehavior='default'
                />
            )
        };
    };

    private _generateOnThumbnailKeyDown = (index: number) => {
        return (event: React.KeyboardEvent) => {
            if (event.which === KeyCodes.Enter || event.which === KeyCodes.Space) {
                event.preventDefault();

                this.goToIndex(index);
            }
        };
    };

    private _generateOnThumbnailClick = (index: number) => {
        return (event: React.MouseEvent<HTMLLIElement>) => {
            event.preventDefault();

            this.goToIndex(index);
        };
    };

    private _mapProductToImageData(product: SimpleProduct): IImageData {
        return {
            src: product.PrimaryImageUrl || '',
        };
    }

    private isFirstItem = () => this.state.activeIndex === 0;

    private isLastItem = () => this.state.mediaGalleryItems && this.state.activeIndex === this.state.mediaGalleryItems.length - 1;
}

export default MediaGallery;